import { initReactI18next } from 'react-i18next';
import i18n, { type i18n as I18nInstance, type Resource } from 'i18next';
import enUI from '@linktivity/link-ui/lang/en';
import jaUI from '@linktivity/link-ui/lang/ja';
import koUI from '@linktivity/link-ui/lang/ko';
import zhUI from '@linktivity/link-ui/lang/zh';
import ztUI from '@linktivity/link-ui/lang/zt';
import { JAPANESE_LOCALE, LOCALE_OPTIONS } from '@link/share/constants';
import { LocaleOption } from '@link/share/services/types';
import enCountry from './country/en';
import jaCountry from './country/ja';
import koCountry from './country/ko';
import zhCountry from './country/zh';
import ztCountry from './country/zt';
import { deepMergeResources, type DeepPartial } from './deepMerge';
import enJapanPrefecture from './japanPrefectures/en';
import jaJapanPrefecture from './japanPrefectures/ja';
import koJapanPrefecture from './japanPrefectures/ko';
import zhJapanPrefecture from './japanPrefectures/zh';
import ztJapanPrefecture from './japanPrefectures/zt';
import shareEnText from './lang/en';
import shareJaText from './lang/ja';
import shareKoText from './lang/ko';
import shareZhText from './lang/zh';
import shareZtText from './lang/zt';
import enLang from './language/en';
import jaLang from './language/ja';
import koLang from './language/ko';
import zhLang from './language/zh';
import ztLang from './language/zt';

export const DEFAULT_LOCALE = JAPANESE_LOCALE;
export const LOCALE_LIST: string[] = LOCALE_OPTIONS.map(locale => locale.value);

export const getLocaleValues = (options: readonly LocaleOption[]): string[] =>
  options.map(locale => locale.value);

interface ResourcesRecord {
  [key: string]: string | string[] | ResourcesRecord | Record<string, unknown>;
}

/**
 * The share-side tree an app may override, per locale: the shared lang file,
 * link-ui's text and the three standalone namespaces (see `translation()`).
 */
type ShareResourceTree = typeof shareEnText &
  typeof enUI & {
    country: typeof enCountry;
    prefecture: typeof enJapanPrefecture;
    lang: typeof enLang;
  };

export type Locale = (typeof LOCALE_OPTIONS)[number]['value'];

/**
 * Per locale and partial: an app may override some locales only. Covering
 * every locale is a review concern the type cannot meaningfully force.
 */
export type LocaleOverrides = Partial<
  Record<Locale, DeepPartial<ShareResourceTree>>
>;

/**
 * Pins an app's overrides to the shared resource shape, so a mistyped key path
 * fails to compile instead of silently never resolving.
 */
export const defineLocaleOverrides = (
  overrides: LocaleOverrides
): LocaleOverrides => overrides;

interface Resources {
  jaText?: ResourcesRecord;
  enText?: ResourcesRecord;
  koText?: ResourcesRecord;
  zhText?: ResourcesRecord;
  ztText?: ResourcesRecord;
  /** Deep-merged last, so it wins over share's text and the app's `xxText`. */
  overrides?: LocaleOverrides;
}

interface ShareLocaleText {
  text: ResourcesRecord;
  ui: ResourcesRecord;
  country: ResourcesRecord;
  prefecture: ResourcesRecord;
  lang: ResourcesRecord;
}

const SHARE_TEXT: Record<Locale, ShareLocaleText> = {
  ja: {
    text: shareJaText,
    ui: jaUI,
    country: jaCountry,
    prefecture: jaJapanPrefecture,
    lang: jaLang
  },
  en: {
    text: shareEnText,
    ui: enUI,
    country: enCountry,
    prefecture: enJapanPrefecture,
    lang: enLang
  },
  ko: {
    text: shareKoText,
    ui: koUI,
    country: koCountry,
    prefecture: koJapanPrefecture,
    lang: koLang
  },
  zh: {
    text: shareZhText,
    ui: zhUI,
    country: zhCountry,
    prefecture: zhJapanPrefecture,
    lang: zhLang
  },
  zt: {
    text: shareZtText,
    ui: ztUI,
    country: ztCountry,
    prefecture: ztJapanPrefecture,
    lang: ztLang
  }
};

// Later layers win: share's text, the app's text, link-ui's text, the
// standalone namespaces, then the app's overrides.
const translation = (
  locale: Locale,
  appText?: ResourcesRecord,
  overrides?: LocaleOverrides
): ResourcesRecord => {
  const { text, ui, country, prefecture, lang } = SHARE_TEXT[locale];
  return deepMergeResources<ResourcesRecord>(
    { ...text, ...appText, ...ui, country, prefecture, lang },
    overrides?.[locale]
  );
};

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export const resources = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText,
  overrides
}: Resources) => ({
  ja: { translation: translation('ja', jaText, overrides) },
  en: { translation: translation('en', enText, overrides) },
  ko: { translation: translation('ko', koText, overrides) },
  zh: { translation: translation('zh', zhText, overrides) },
  zt: { translation: translation('zt', ztText, overrides) }
});

export function setLocale(locale: string): string {
  void i18n.changeLanguage(locale);
  document.documentElement.setAttribute('lang', locale);
  localStorage.setItem('locale', locale);
  return locale;
}

function getLocaleParams(supportedLocales: string[]) {
  const searchParams = new URLSearchParams(location.search);
  const lang = searchParams.get('lang') || '';
  if (supportedLocales.includes(lang)) {
    return lang;
  }
}

interface InitI18nOptions extends Resources {
  defaultLocale?: string;
  supportedLocales?: string[];
}

export const initI18n = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText,
  overrides,
  defaultLocale = DEFAULT_LOCALE,
  supportedLocales = LOCALE_LIST
}: InitI18nOptions) => {
  void i18n.use(initReactI18next).init({
    lng: defaultLocale,
    returnNull: false,
    // React escapes text nodes already; i18next's escaping would turn an
    // interpolated "9/15 (Mon)" into "9&#x2F;15 (Mon)".
    interpolation: {
      escapeValue: false
    },
    resources: resources({
      jaText,
      enText,
      koText,
      zhText,
      ztText,
      overrides
    })
  });

  const resolvedLocale =
    getLocaleParams(supportedLocales) ||
    localStorage.getItem('locale') ||
    defaultLocale;
  setLocale(
    supportedLocales.includes(resolvedLocale) ? resolvedLocale : defaultLocale
  );
};

/**
 * Every locale share has text for, as a readonly tuple of the `Locale` union.
 */
export const LOCALES: readonly Locale[] = LOCALE_OPTIONS.map(
  locale => locale.value
);

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

/**
 * Narrow an untrusted value (a route param, a header) to a supported locale,
 * falling back to `fallback` when it is not one.
 */
export const resolveLocale = (
  value: unknown,
  fallback: Locale = DEFAULT_LOCALE
): Locale => (isLocale(value) ? value : fallback);

export interface CreateI18nOptions {
  /** Locale to render in. Already narrowed; see `resolveLocale`. */
  locale: Locale;
  /** Built once with `resources()` and reused, so the merge is not repeated per request. */
  resources: Resource;
  supportedLocales?: readonly Locale[];
  fallbackLocale?: Locale;
}

/**
 * An isolated i18next instance bound to one locale, for server rendering.
 *
 * The default export is one module-level instance that `initI18n()` and
 * `setLocale()` mutate, which is right for a browser and wrong for a server:
 * concurrent requests in different languages would race on one
 * `changeLanguage()`, and `setLocale()` writes `document` and `localStorage`.
 * Call this once per request on the server and once at hydration instead.
 *
 * `initReactI18next` is attached to the instance, not to the singleton, so
 * instances never see each other's state. `init` completes synchronously —
 * the resources are in memory and no backend or detector is registered — so
 * rendering can start as soon as this returns.
 */
export function createI18n({
  locale,
  resources: prebuilt,
  supportedLocales = LOCALES,
  fallbackLocale = DEFAULT_LOCALE
}: CreateI18nOptions): I18nInstance {
  const instance = i18n.createInstance();
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: fallbackLocale,
    supportedLngs: [...supportedLocales],
    returnNull: false,
    resources: prebuilt,
    // Same reason as initI18n: React escapes text nodes already.
    interpolation: {
      escapeValue: false
    }
  });
  return instance;
}

export default i18n;
